import type { InventoryStatus } from '@/api/inventory';
import { resolvePhantom, transferToOwnAsset, transferToRentalEquipment } from '@/api/inventory';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useBarcodeLookup, useInventoryItem } from '@/hooks/use-inventory';
import { useTabUrlSync } from '@/hooks/use-tab-url-sync';
import { useTranslation } from '@kgc/i18n';
import { DetailTabPanel, DetailTabsNav } from '@kgc/ui';
import { Suspense, lazy, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const EquipmentProfitTab = lazy(() => import('./EquipmentProfitTab'));

const TYPE_LABELS: Record<string, string> = {
  PRODUCT: 'inventory.typeLabels.product',
  RENTAL_EQUIPMENT: 'inventory.typeLabels.rentalEquipment',
  PART: 'inventory.typeLabels.part',
  CONSUMABLE: 'inventory.typeLabels.consumable',
};

export function InventoryDetailPage() {
  const { t } = useTranslation();

  const STATUS_LABELS: Record<InventoryStatus, { label: string; color: string }> = {
    AVAILABLE: {
      label: t('inventory.available'),
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    },
    RESERVED: {
      label: t('status.reserved'),
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    },
    IN_TRANSIT: {
      label: t('inventory.statusLabels.inTransit'),
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    },
    IN_SERVICE: {
      label: t('status.inService'),
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    },
    SOLD: {
      label: t('inventory.statusLabels.sold'),
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    },
    RENTED: {
      label: t('status.rentedOut'),
      color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
    },
    DAMAGED: {
      label: t('inventory.statusLabels.damaged'),
      color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    },
    LOST: {
      label: t('inventory.statusLabels.lost'),
      color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    },
    SCRAPPED: {
      label: t('inventory.statusLabels.scrapped'),
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    },
  };
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { item, isLoading, error, refetch } = useInventoryItem(id);
  const [isEditing, setIsEditing] = useState(false);
  // Server-mode-agnoszt. tab-state URL-syncelve (?tab=…) a @kgc/ui DetailTabsNav/DetailTabPanel-hez.
  const [activeTab, setActiveTab] = useTabUrlSync('tab', 'details');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferResult, setTransferResult] = useState<{
    type: 'rental' | 'ownAsset';
    rentalEquipmentId?: string;
    fixedAssetId?: string;
  } | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [showTransferConfirm, setShowTransferConfirm] = useState<'rental' | 'ownAsset' | null>(
    null
  );
  const isRentalEquipment = item?.type === 'RENTAL_EQUIPMENT';
  const isPhantom = item?.quantity === 0 && item?.status === 'AVAILABLE';

  // INV-FG-7-1: Barcode lookup
  const {
    result: barcodeResult,
    isLoading: barcodeLoading,
    error: barcodeError,
    lookup: barcodeLookup,
    clear: barcodeClear,
  } = useBarcodeLookup();
  const [barcodeInput, setBarcodeInput] = useState('');

  // INV-FG-7-4: Phantom resolution
  const [phantomLoading, setPhantomLoading] = useState(false);
  const [phantomResult, setPhantomResult] = useState<string | null>(null);

  const handlePhantomResolve = async (action: 'SCRAP' | 'RECOUNT', actualQuantity?: number) => {
    if (!id) return;
    setPhantomLoading(true);
    setPhantomResult(null);
    try {
      await resolvePhantom(id, action, actualQuantity);
      setPhantomResult(t('inventory.phantom.resolved'));
      void refetch();
    } catch {
      setPhantomResult(t('common.error'));
    } finally {
      setPhantomLoading(false);
    }
  };

  const handleBarcodeScan = async () => {
    if (!barcodeInput.trim()) return;
    const result = await barcodeLookup(barcodeInput);
    if (result) {
      // Navigate to the found item
      navigate(`/inventory/${result.inventoryItemId}`);
    }
  };

  const handleTransfer = async (type: 'rental' | 'ownAsset') => {
    if (!id) return;
    setTransferLoading(true);
    setTransferError(null);
    setTransferResult(null);
    setShowTransferConfirm(null);
    try {
      if (type === 'rental') {
        const res = await transferToRentalEquipment(id);
        setTransferResult({
          type: 'rental',
          rentalEquipmentId: res.data.rentalEquipmentId,
          fixedAssetId: res.data.fixedAssetId,
        });
      } else {
        const res = await transferToOwnAsset(id);
        setTransferResult({
          type: 'ownAsset',
          fixedAssetId: res.data.fixedAssetId,
        });
      }
      void refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('inventory.transfer.error');
      setTransferError(message);
    } finally {
      setTransferLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="w-96 text-center">
          <CardContent className="pt-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="w-96 text-center">
          <CardContent className="pt-6">
            <p className="text-red-500 mb-4">{t('page.errorPrefix', { message: error })}</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => refetch()}>{t('common.retry')}</Button>
              <Button variant="outline" onClick={() => navigate('/inventory')}>
                {t('inventory.backToList')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not found state
  if (!item) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="w-96 text-center">
          <CardContent className="pt-6">
            <p className="text-gray-500 dark:text-gray-400">{t('inventory.itemNotFound')}</p>
            <Button variant="outline" onClick={() => navigate('/inventory')} className="mt-4">
              Vissza a listához
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('hu-HU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const stockValue = item.quantity * (item.purchasePrice || 0);
  const reservedQty = item.reservedQuantity ?? 0;
  const availableQuantity = item.quantity - reservedQty;

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white dark:bg-[#1a4f5c] shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/inventory')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {item.productName || 'N/A'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {item.productSku || item.serialNumber || '-'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsEditing(!isEditing)}>
              {isEditing ? t('common.cancel') : t('common.edit')}
            </Button>
            {isEditing && (
              <Button className="bg-primary hover:bg-primary/90">{t('common.save')}</Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Tab navigation for rental equipment (megosztott @kgc/ui DetailTabsNav foundation) */}
        {isRentalEquipment && (
          <DetailTabsNav
            idPrefix="equipment"
            ariaLabel={t('inventory.views')}
            value={activeTab}
            onValueChange={setActiveTab}
            tabs={[
              { key: 'details', label: t('common.details') },
              { key: 'profitability', label: t('inventory.profitability') },
            ]}
          />
        )}

        {/* Profitability tab content (helyben-maradó panel, DetailTabPanel wrapper) */}
        {isRentalEquipment && id && (
          <DetailTabPanel tabKey="profitability" activeKey={activeTab} idPrefix="equipment">
            <Suspense fallback={<div className="animate-pulse h-64 bg-muted rounded-lg" />}>
              <EquipmentProfitTab equipmentId={id} />
            </Suspense>
          </DetailTabPanel>
        )}

        {/* Details tab content (helyben-maradó panel) */}
        <DetailTabPanel
          tabKey="details"
          activeKey={isRentalEquipment ? activeTab : 'details'}
          idPrefix="equipment"
          className="grid gap-6 lg:grid-cols-3"
        >
          {/* Main info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic info */}
            <Card>
              <CardHeader>
                <CardTitle>{t('inventory.basicData')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('inventory.sku')}
                    </label>
                    <p className="text-lg font-medium">{item.productSku || '-'}</p>
                  </div>
                  {item.serialNumber && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('inventory.serialNumber')}
                      </label>
                      <p className="font-mono text-lg">{item.serialNumber}</p>
                    </div>
                  )}
                  {item.batchNumber && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('inventory.batchNumber')}
                      </label>
                      <p className="font-mono text-lg">{item.batchNumber}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('common.type')}
                    </label>
                    <p className="text-lg">{t(TYPE_LABELS[item.type] ?? '') || item.type}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            {item.purchasePrice && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('inventory.pricing')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('inventory.purchasePrice')}
                      </label>
                      {isEditing ? (
                        <Input type="number" defaultValue={item.purchasePrice} className="mt-1" />
                      ) : (
                        <p className="text-lg font-medium">{formatPrice(item.purchasePrice)}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stock levels */}
            <Card>
              <CardHeader>
                <CardTitle>{t('inventory.stockLevels')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {item.minStockLevel !== undefined && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('inventory.minimum')}
                      </label>
                      {isEditing ? (
                        <Input type="number" defaultValue={item.minStockLevel} className="mt-1" />
                      ) : (
                        <p className="text-lg">
                          {item.minStockLevel} {item.unit}
                        </p>
                      )}
                    </div>
                  )}
                  {item.maxStockLevel !== undefined && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('inventory.maximum')}
                      </label>
                      {isEditing ? (
                        <Input type="number" defaultValue={item.maxStockLevel} className="mt-1" />
                      ) : (
                        <p className="text-lg">
                          {item.maxStockLevel} {item.unit}
                        </p>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('inventory.warehouse')}
                    </label>
                    <p className="text-lg">{item.warehouseName || '-'}</p>
                  </div>
                  {item.locationCode && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('inventory.location')}
                      </label>
                      {isEditing ? (
                        <Input type="text" defaultValue={item.locationCode} className="mt-1" />
                      ) : (
                        <p className="text-lg font-mono">{item.locationCode}</p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Current stock */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <span
                    className={`inline-block rounded-full px-3 py-1 text-sm font-medium ${STATUS_LABELS[item.status]?.color || 'bg-muted text-foreground'}`}
                  >
                    {STATUS_LABELS[item.status]?.label || item.status}
                  </span>
                  <p className="mt-4 text-4xl font-bold text-gray-900 dark:text-white">
                    {item.quantity}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400">
                    {item.unit} {t('inventory.total')}
                  </p>

                  <div className="mt-6 grid grid-cols-2 gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div>
                      <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                        {reservedQty}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('inventory.reserved')}
                      </p>
                      {reservedQty > 0 && (
                        <span className="mt-1 inline-block rounded-full border border-orange-500 px-2 py-0.5 text-xs font-medium text-orange-600 dark:text-orange-400">
                          {t('inventory.reservedForWorksheet')}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {availableQuantity}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('inventory.available')}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stock value */}
            {item.purchasePrice && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('inventory.stockValue')}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-blue-600">{formatPrice(stockValue)}</p>
                  <p className="text-sm text-muted-foreground">
                    ({item.quantity} × {formatPrice(item.purchasePrice)})
                  </p>
                </CardContent>
              </Card>
            )}

            {/* INV-FG-7-1: Barcode scanner */}
            <Card>
              <CardContent className="pt-6">
                <p className="mb-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('inventory.barcode.scan')}
                </p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder={t('inventory.barcode.scanPlaceholder')}
                    value={barcodeInput}
                    onChange={e => {
                      setBarcodeInput(e.target.value);
                      barcodeClear();
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') void handleBarcodeScan();
                    }}
                    className="flex-1"
                    data-testid="barcode-input"
                  />
                  <Button
                    size="sm"
                    disabled={barcodeLoading || !barcodeInput.trim()}
                    onClick={() => void handleBarcodeScan()}
                  >
                    {barcodeLoading ? '...' : '🔍'}
                  </Button>
                </div>
                {barcodeError && (
                  <p className="mt-2 text-sm text-red-500">{t('inventory.barcode.notFound')}</p>
                )}
                {barcodeResult && (
                  <div className="mt-2 rounded-lg bg-green-50 p-2 text-sm dark:bg-green-900/20">
                    <p className="font-medium text-green-800 dark:text-green-300">
                      {t('inventory.barcode.found')}: {barcodeResult.productName}
                    </p>
                    <p className="text-green-600 dark:text-green-400">
                      {barcodeResult.quantity} {barcodeResult.unit} — {barcodeResult.warehouseName}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* INV-FG-7-4: Phantom resolution */}
            {isPhantom && (
              <Card className="border-amber-200 dark:border-amber-800">
                <CardContent className="pt-6">
                  <p className="mb-3 text-sm font-medium text-amber-600 dark:text-amber-400">
                    {t('inventory.phantom.title')}
                  </p>
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={phantomLoading}
                      onClick={() => void handlePhantomResolve('SCRAP')}
                    >
                      {t('inventory.phantom.scrap')}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={phantomLoading}
                      onClick={() => {
                        const qty = window.prompt(t('inventory.phantom.actualQuantity'));
                        if (qty !== null) {
                          void handlePhantomResolve('RECOUNT', parseInt(qty, 10) || 0);
                        }
                      }}
                    >
                      {t('inventory.phantom.recount')}
                    </Button>
                  </div>
                  {phantomResult && (
                    <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                      {phantomResult}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quick actions */}
            <Card>
              <CardContent className="pt-6">
                <p className="mb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('inventory.quickActions')}
                </p>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/goods-receipt/new')}
                  >
                    {t('inventory.receiveStock')}
                  </Button>
                  <Button variant="outline" className="w-full">
                    {t('inventory.issueStock')}
                  </Button>
                  <Button variant="outline" className="w-full">
                    {t('inventory.correction')}
                  </Button>
                </div>

                {/* Q-TRF-1: Transfer actions (áru → bérgép / saját eszköz) */}
                {item.type === 'PRODUCT' && item.quantity > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="mb-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('inventory.transfer.title')}
                    </p>
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        className="w-full text-left"
                        disabled={transferLoading}
                        onClick={() => setShowTransferConfirm('rental')}
                        data-testid="transfer-to-rental-btn"
                      >
                        {t('inventory.transfer.toRental')}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full text-left"
                        disabled={transferLoading}
                        onClick={() => setShowTransferConfirm('ownAsset')}
                        data-testid="transfer-to-own-asset-btn"
                      >
                        {t('inventory.transfer.toOwnAsset')}
                      </Button>
                    </div>

                    {/* Confirmation dialog */}
                    {showTransferConfirm && (
                      <div
                        className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
                        data-testid="transfer-confirm-dialog"
                      >
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                          {showTransferConfirm === 'rental'
                            ? t('inventory.transfer.confirmRental')
                            : t('inventory.transfer.confirmOwnAsset')}
                        </p>
                        <p className="text-xs text-yellow-600 dark:text-yellow-400 mb-3">
                          {t('inventory.transfer.confirmNote')}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-yellow-600 hover:bg-yellow-700 text-white"
                            disabled={transferLoading}
                            onClick={() => void handleTransfer(showTransferConfirm)}
                            data-testid="transfer-confirm-btn"
                          >
                            {transferLoading ? '...' : t('common.confirm')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowTransferConfirm(null)}
                            data-testid="transfer-cancel-btn"
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Transfer result */}
                    {transferResult && (
                      <div
                        className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
                        data-testid="transfer-result"
                      >
                        <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
                          {t('inventory.transfer.success')}
                        </p>
                        {transferResult.rentalEquipmentId && (
                          <span className="inline-block text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded px-2 py-0.5 mr-1">
                            {t('inventory.transfer.rentalCardCreated')}
                          </span>
                        )}
                        {transferResult.fixedAssetId && (
                          <span className="inline-block text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded px-2 py-0.5">
                            {t('inventory.transfer.ecsStarted')}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Transfer error */}
                    {transferError && (
                      <div
                        className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                        data-testid="transfer-error"
                      >
                        <p className="text-sm text-red-800 dark:text-red-200">{transferError}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dates */}
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>{t('inventory.createdAt')}:</span>
                    <span>{formatDate(item.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('inventory.updatedAt')}:</span>
                    <span>{formatDate(item.updatedAt)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </DetailTabPanel>
      </main>
    </div>
  );
}
